import React from 'react';

// Auto-layout utility for positioning nodes in flows
export class AutoLayout {
  static NODE_WIDTH = 240;

  static NODE_HEIGHT = 92;

  static HORIZONTAL_SPACING = 300;

  static VERTICAL_SPACING = 150;

  static START_X = 100;

  static START_Y = 100;

  /**
   * Apply auto-layout to a set of nodes and edges
   * @param {Array} nodes - Array of node objects
   * @param {Array} edges - Array of edge objects
   * @param {Object} options - Layout options
   * @returns {Array} - Nodes with updated positions
   */
  static applyLayout(nodes, edges, options = {}) {
    if (!nodes || nodes.length === 0) return nodes;

    const {
      direction = 'horizontal', // 'horizontal', 'vertical', 'hierarchical', 'compact'
      orientation = 'top-down', // for hierarchical: 'top-down' (vertical flow) or 'left-right' (horizontal flow)
      spacing = { x: this.HORIZONTAL_SPACING, y: this.VERTICAL_SPACING },
      startPosition = { x: this.START_X, y: this.START_Y },
      centerNodes = true,
    } = options;

    // Create a copy of nodes to avoid mutating the original
    const layoutNodes = nodes.map((node) => ({ ...node }));

    switch (direction) {
      case 'horizontal':
        return this.horizontalLayout(layoutNodes, edges, spacing, startPosition, centerNodes);
      case 'vertical':
        return this.verticalLayout(layoutNodes, edges, spacing, startPosition, centerNodes);
      case 'hierarchical':
        return this.hierarchicalLayout(layoutNodes, edges, spacing, startPosition, { orientation });
      case 'compact':
        return this.compactLayout(layoutNodes, edges, spacing, startPosition);
      default:
        return this.horizontalLayout(layoutNodes, edges, spacing, startPosition, centerNodes);
    }
  }

  /**
   * Horizontal layout - arranges nodes in a horizontal flow
   */
  static horizontalLayout(nodes, edges, spacing, startPosition, centerNodes) {
    // Find start nodes (nodes with no incoming edges)
    const startNodes = this.findStartNodes(nodes, edges);

    if (startNodes.length === 0) {
      // If no clear start nodes, use the first node
      startNodes.push(nodes[0]);
    }

    // Create a graph structure for traversal
    const graph = this.buildGraph(nodes, edges);
    const visited = new Set();
    const positions = new Map();

    // Position start nodes
    startNodes.forEach((node, index) => {
      positions.set(node.id, {
        x: startPosition.x,
        y: startPosition.y + (index * spacing.y),
      });
    });

    // BFS traversal to position remaining nodes
    const queue = [...startNodes];
    visited.add(startNodes[0].id);

    while (queue.length > 0) {
      const currentNode = queue.shift();
      const currentPos = positions.get(currentNode.id);

      if (!currentPos) continue;

      const outgoingEdges = edges.filter((edge) => edge.source === currentNode.id);

      outgoingEdges.forEach((edge, index) => {
        const targetNode = nodes.find((n) => n.id === edge.target);
        if (!targetNode || visited.has(targetNode.id)) return;

        // Position target node to the right of current node
        const targetPos = {
          x: currentPos.x + spacing.x,
          y: currentPos.y + (index * spacing.y * 0.5), // Slight vertical offset for multiple connections
        };

        positions.set(targetNode.id, targetPos);
        visited.add(targetNode.id);
        queue.push(targetNode);
      });
    }

    // Handle any remaining unvisited nodes
    nodes.forEach((node) => {
      if (!visited.has(node.id)) {
        const maxX = Math.max(...Array.from(positions.values()).map((p) => p.x));
        positions.set(node.id, {
          x: maxX + spacing.x,
          y: startPosition.y,
        });
      }
    });

    // Apply positions to nodes
    return nodes.map((node) => ({
      ...node,
      position: positions.get(node.id) || { x: startPosition.x, y: startPosition.y },
    }));
  }

  /**
   * Vertical layout - arranges nodes in a vertical flow
   */
  static verticalLayout(nodes, edges, spacing, startPosition, centerNodes) {
    // Similar to horizontal but with x and y swapped
    const startNodes = this.findStartNodes(nodes, edges);

    if (startNodes.length === 0) {
      startNodes.push(nodes[0]);
    }

    const graph = this.buildGraph(nodes, edges);
    const visited = new Set();
    const positions = new Map();

    // Position start nodes
    startNodes.forEach((node, index) => {
      positions.set(node.id, {
        x: startPosition.x + (index * spacing.x),
        y: startPosition.y,
      });
    });

    // BFS traversal
    const queue = [...startNodes];
    visited.add(startNodes[0].id);

    while (queue.length > 0) {
      const currentNode = queue.shift();
      const currentPos = positions.get(currentNode.id);

      if (!currentPos) continue;

      const outgoingEdges = edges.filter((edge) => edge.source === currentNode.id);

      outgoingEdges.forEach((edge, index) => {
        const targetNode = nodes.find((n) => n.id === edge.target);
        if (!targetNode || visited.has(targetNode.id)) return;

        const targetPos = {
          x: currentPos.x + (index * spacing.x * 0.5),
          y: currentPos.y + spacing.y,
        };

        positions.set(targetNode.id, targetPos);
        visited.add(targetNode.id);
        queue.push(targetNode);
      });
    }

    // Handle remaining nodes
    nodes.forEach((node) => {
      if (!visited.has(node.id)) {
        const maxY = Math.max(...Array.from(positions.values()).map((p) => p.y));
        positions.set(node.id, {
          x: startPosition.x,
          y: maxY + spacing.y,
        });
      }
    });

    return nodes.map((node) => ({
      ...node,
      position: positions.get(node.id) || { x: startPosition.x, y: startPosition.y },
    }));
  }

  static hierarchicalLayout(nodes, edges, spacing, startPosition, options = {}) {
    const { orientation = 'top-down' } = options;
    const nodeWidth = spacing.nodeWidth ?? this.NODE_WIDTH;
    const nodeHeight = spacing.nodeHeight ?? this.NODE_HEIGHT;
    const xGap = spacing.x ?? 88;
    const yGap = spacing.y ?? 112;
    const hStep = nodeWidth + xGap;
    const vStep = nodeHeight + yGap;

    const levels = new Map();
    const inDegree = new Map();
    const graph = new Map();
    const incoming = new Map();
    const outgoing = new Map();

    nodes.forEach((node) => {
      inDegree.set(node.id, 0);
      graph.set(node.id, []);
      incoming.set(node.id, []);
      outgoing.set(node.id, []);
    });

    edges.forEach((edge) => {
      if (!graph.has(edge.source)) graph.set(edge.source, []);
      graph.get(edge.source).push(edge.target);
      inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
      incoming.get(edge.target).push(edge.source);
      outgoing.get(edge.source).push(edge.target);
    });

    const queue = [];
    nodes.forEach((node) => {
      if (inDegree.get(node.id) === 0) {
        queue.push({ nodeId: node.id, level: 0 });
        levels.set(node.id, 0);
      }
    });

    while (queue.length > 0) {
      const { nodeId, level } = queue.shift();
      const currentLevel = levels.get(nodeId) || 0;

      const neighbors = graph.get(nodeId) || [];
      neighbors.forEach((neighborId) => {
        const newLevel = currentLevel + 1;
        const existingLevel = levels.get(neighborId);

        if (existingLevel === undefined || newLevel > existingLevel) {
          levels.set(neighborId, newLevel);
        }

        inDegree.set(neighborId, inDegree.get(neighborId) - 1);

        if (inDegree.get(neighborId) === 0) {
          queue.push({ nodeId: neighborId, level: newLevel });
        }
      });
    }

    nodes.forEach((node) => {
      if (!levels.has(node.id)) {
        levels.set(node.id, 0);
      }
    });

    const levelGroups = new Map();
    nodes.forEach((node) => {
      const level = levels.get(node.id) || 0;
      if (!levelGroups.has(level)) {
        levelGroups.set(level, []);
      }
      levelGroups.get(level).push(node);
    });

    const levelKeys = [...levelGroups.keys()].sort((a, b) => a - b);
    levelKeys.forEach((level) => {
      levelGroups.get(level).sort((a, b) => a.id.localeCompare(b.id));
    });

    const positions = new Map();
    const centerX = (nodeId) => {
      const pos = positions.get(nodeId);
      return pos ? pos.x + nodeWidth / 2 : 0;
    };
    const barycenter = (neighborIds, fallbackId) => {
      if (!neighborIds?.length) return centerX(fallbackId);
      return neighborIds.reduce((sum, id) => sum + centerX(id), 0) / neighborIds.length;
    };

    levelKeys.forEach((level) => {
      const levelNodes = levelGroups.get(level);
      const levelPrimary = orientation === 'top-down' ? startPosition.y + (level * vStep) : startPosition.x + (level * hStep);
      levelNodes.forEach((node, index) => {
        const cx = startPosition.x - ((levelNodes.length - 1) * hStep) / 2 + (index * hStep);
        if (orientation === 'top-down') {
          positions.set(node.id, { x: cx - nodeWidth / 2, y: levelPrimary });
        } else {
          positions.set(node.id, { x: levelPrimary, y: cx - nodeHeight / 2 });
        }
      });
    });

    for (let sweep = 0; sweep < 12; sweep += 1) {
      const downward = sweep % 2 === 0;
      const iterLevels = downward ? levelKeys : [...levelKeys].reverse();
      iterLevels.forEach((level) => {
        const levelNodes = levelGroups.get(level);
        levelNodes.sort((a, b) => {
          const aNeighbors = downward ? incoming.get(a.id) : outgoing.get(a.id);
          const bNeighbors = downward ? incoming.get(b.id) : outgoing.get(b.id);
          const diff = barycenter(aNeighbors, a.id) - barycenter(bNeighbors, b.id);
          return diff !== 0 ? diff : a.id.localeCompare(b.id);
        });
      });
    }

    if (orientation === 'top-down') {
      levelKeys.forEach((level) => {
        const levelNodes = levelGroups.get(level);
        const levelY = startPosition.y + (level * vStep);
        levelNodes.forEach((node, index) => {
          const cx = startPosition.x - ((levelNodes.length - 1) * hStep) / 2 + (index * hStep);
          positions.set(node.id, { x: cx - nodeWidth / 2, y: levelY });
        });
        this.resolveLevelOverlaps(
          levelNodes.map((node) => node.id),
          positions,
          nodeWidth,
          xGap,
          'x',
        );
      });

      const allLeft = nodes.map((node) => positions.get(node.id).x);
      const allRight = nodes.map((node) => positions.get(node.id).x + nodeWidth);
      const graphCenter = (Math.min(...allLeft) + Math.max(...allRight)) / 2;
      const shiftX = startPosition.x - graphCenter;
      nodes.forEach((node) => {
        const pos = positions.get(node.id);
        positions.set(node.id, { x: pos.x + shiftX, y: pos.y });
      });
    } else {
      levelKeys.forEach((level) => {
        const levelNodes = levelGroups.get(level);
        const levelX = startPosition.x + (level * hStep);
        levelNodes.forEach((node, index) => {
          const cy = startPosition.y - ((levelNodes.length - 1) * hStep) / 2 + (index * hStep);
          positions.set(node.id, { x: levelX, y: cy - nodeHeight / 2 });
        });
        this.resolveLevelOverlaps(
          levelNodes.map((node) => node.id),
          positions,
          nodeHeight,
          yGap,
          'y',
        );
      });

      const allTop = nodes.map((node) => positions.get(node.id).y);
      const allBottom = nodes.map((node) => positions.get(node.id).y + nodeHeight);
      const graphCenter = (Math.min(...allTop) + Math.max(...allBottom)) / 2;
      const shiftY = startPosition.y - graphCenter;
      nodes.forEach((node) => {
        const pos = positions.get(node.id);
        positions.set(node.id, { x: pos.x, y: pos.y + shiftY });
      });
    }

    return nodes.map((node) => ({
      ...node,
      position: positions.get(node.id) || { x: startPosition.x, y: startPosition.y },
    }));
  }

  /**
   * Push nodes apart on one axis when spacing is too tight.
   */
  static resolveLevelOverlaps(nodeIds, positions, nodeSize, gap, axis) {
    const sorted = [...nodeIds].sort((a, b) => positions.get(a)[axis] - positions.get(b)[axis]);
    for (let i = 1; i < sorted.length; i += 1) {
      const prevId = sorted[i - 1];
      const currId = sorted[i];
      const prev = positions.get(prevId);
      const curr = positions.get(currId);
      const minCoord = prev[axis] + nodeSize + gap;
      if (curr[axis] < minCoord) {
        positions.set(currId, { ...curr, [axis]: minCoord });
      }
    }
  }

  /**
   * Stagger parallel smooth-step edges from the same source to reduce overlap.
   */
  static applyEdgeOffsets(edges, nodePositions, options = {}) {
    const nodeWidth = options.nodeWidth ?? this.NODE_WIDTH;
    const offsetStep = options.edgeOffset ?? 20;
    const centerX = (nodeId) => (nodePositions.get(nodeId)?.x ?? 0) + nodeWidth / 2;
    const bySource = new Map();

    edges.forEach((edge) => {
      if (!bySource.has(edge.source)) bySource.set(edge.source, []);
      bySource.get(edge.source).push(edge);
    });

    const offsetById = new Map();
    bySource.forEach((group) => {
      if (group.length <= 1) return;
      const sorted = [...group].sort((a, b) => centerX(a.target) - centerX(b.target));
      sorted.forEach((edge, index) => {
        const offset = (index - (sorted.length - 1) / 2) * offsetStep;
        offsetById.set(edge.id, offset);
      });
    });

    return edges.map((edge) => {
      const offset = offsetById.get(edge.id);
      if (offset == null) {
        const { pathOptions, ...rest } = edge;
        if (!pathOptions?.offset) return edge;
        const nextPathOptions = { ...pathOptions };
        delete nextPathOptions.offset;
        return Object.keys(nextPathOptions).length
          ? { ...rest, pathOptions: nextPathOptions }
          : rest;
      }
      return {
        ...edge,
        pathOptions: {
          ...(edge.pathOptions || {}),
          borderRadius: edge.pathOptions?.borderRadius ?? 16,
          offset,
        },
      };
    });
  }

  /**
   * Compact layout - uses both dimensions for a balanced, readable grid
   * Adapts to graph shape: deep graphs flow top-down, wide graphs flow left-right
   */
  static compactLayout(nodes, edges, spacing, startPosition) {
    const inDegree = new Map();
    const graph = new Map();

    nodes.forEach((node) => {
      inDegree.set(node.id, 0);
      graph.set(node.id, []);
    });

    edges.forEach((edge) => {
      if (!graph.has(edge.source)) graph.set(edge.source, []);
      graph.get(edge.source).push(edge.target);
      inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
    });

    const levels = new Map();
    const queue = [];
    nodes.forEach((node) => {
      if (inDegree.get(node.id) === 0) {
        queue.push({ nodeId: node.id, level: 0 });
        levels.set(node.id, 0);
      }
    });

    while (queue.length > 0) {
      const { nodeId, level } = queue.shift();
      const currentLevel = levels.get(nodeId) ?? level;
      (graph.get(nodeId) || []).forEach((neighborId) => {
        const newLevel = currentLevel + 1;
        const existing = levels.get(neighborId);
        if (existing === undefined || newLevel > existing) levels.set(neighborId, newLevel);
        inDegree.set(neighborId, inDegree.get(neighborId) - 1);
        if (inDegree.get(neighborId) === 0) {
          queue.push({ nodeId: neighborId, level: newLevel });
        }
      });
    }

    nodes.forEach((node) => {
      if (!levels.has(node.id)) levels.set(node.id, 0);
    });

    const levelGroups = new Map();
    nodes.forEach((node) => {
      const lvl = levels.get(node.id) || 0;
      if (!levelGroups.has(lvl)) levelGroups.set(lvl, []);
      levelGroups.get(lvl).push(node);
    });

    const levelCount = levelGroups.size;
    const maxNodesInLevel = Math.max(...Array.from(levelGroups.values()).map((arr) => arr.length), 1);

    const useTopDown = levelCount >= maxNodesInLevel;
    const primarySpacing = useTopDown ? spacing.y : spacing.x;
    const secondarySpacing = useTopDown ? spacing.x : spacing.y;

    const positions = new Map();
    levelGroups.forEach((levelNodes, level) => {
      levelNodes.forEach((node, index) => {
        if (useTopDown) {
          const levelY = startPosition.y + (level * primarySpacing);
          const levelWidth = (levelNodes.length - 1) * secondarySpacing;
          const x = startPosition.x - (levelWidth / 2) + (index * secondarySpacing);
          positions.set(node.id, { x, y: levelY });
        } else {
          const levelX = startPosition.x + (level * primarySpacing);
          const levelHeight = (levelNodes.length - 1) * secondarySpacing;
          const y = startPosition.y - (levelHeight / 2) + (index * secondarySpacing);
          positions.set(node.id, { x: levelX, y });
        }
      });
    });

    return nodes.map((node) => ({
      ...node,
      position: positions.get(node.id) || { x: startPosition.x, y: startPosition.y },
    }));
  }

  /**
   * Find nodes that have no incoming edges (start nodes)
   */
  static findStartNodes(nodes, edges) {
    const hasIncomingEdge = new Set();
    edges.forEach((edge) => {
      hasIncomingEdge.add(edge.target);
    });

    return nodes.filter((node) => !hasIncomingEdge.has(node.id));
  }

  /**
   * Build a graph structure from nodes and edges
   */
  static buildGraph(nodes, edges) {
    const graph = new Map();

    nodes.forEach((node) => {
      graph.set(node.id, []);
    });

    edges.forEach((edge) => {
      if (graph.has(edge.source)) {
        graph.get(edge.source).push(edge.target);
      }
    });

    return graph;
  }

  /**
   * Center all nodes around a given point
   */
  static centerNodes(nodes, centerX, centerY) {
    if (nodes.length === 0) return nodes;

    // Calculate current bounds
    const bounds = this.calculateBounds(nodes);
    const currentCenterX = (bounds.minX + bounds.maxX) / 2;
    const currentCenterY = (bounds.minY + bounds.maxY) / 2;

    // Calculate offset to center
    const offsetX = centerX - currentCenterX;
    const offsetY = centerY - currentCenterY;

    // Apply offset to all nodes
    return nodes.map((node) => ({
      ...node,
      position: {
        x: node.position.x + offsetX,
        y: node.position.y + offsetY,
      },
    }));
  }

  /**
   * Calculate bounding box for a set of nodes
   */
  static calculateBounds(nodes) {
    if (nodes.length === 0) {
      return {
        minX: 0, minY: 0, maxX: 0, maxY: 0,
      };
    }

    const xPositions = nodes.map((node) => node.position.x);
    const yPositions = nodes.map((node) => node.position.y);

    return {
      minX: Math.min(...xPositions),
      minY: Math.min(...yPositions),
      maxX: Math.max(...xPositions),
      maxY: Math.max(...yPositions),
    };
  }

  /**
   * Auto-layout with smart spacing based on node content
   */
  static smartLayout(nodes, edges, options = {}) {
    const {
      minSpacing = { x: 250, y: 120 },
      maxSpacing = { x: 400, y: 200 },
      contentAware = true,
    } = options;

    // Apply basic layout first
    let layoutNodes = this.applyLayout(nodes, edges, options);

    if (contentAware) {
      // Adjust spacing based on node labels/content
      layoutNodes = this.adjustSpacingForContent(layoutNodes, minSpacing, maxSpacing);
    }

    return layoutNodes;
  }

  /**
   * Adjust spacing based on node content length
   */
  static adjustSpacingForContent(nodes, minSpacing, maxSpacing) {
    return nodes.map((node, index) => {
      if (index === 0) return node;

      const prevNode = nodes[index - 1];
      const labelLength = (node.data?.label || node.label || '').length;

      // Adjust spacing based on label length
      const spacingMultiplier = Math.min(1 + (labelLength / 50), 2);
      const adjustedSpacing = {
        x: Math.min(minSpacing.x * spacingMultiplier, maxSpacing.x),
        y: Math.min(minSpacing.y * spacingMultiplier, maxSpacing.y),
      };

      return {
        ...node,
        position: {
          x: prevNode.position.x + adjustedSpacing.x,
          y: prevNode.position.y + (index % 2 === 0 ? 0 : adjustedSpacing.y * 0.3),
        },
      };
    });
  }
}

export default AutoLayout;
